import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Well } from '../types/api';

export const wellsKeys = {
  all: ['wells'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...wellsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) => [...wellsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useWellsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: wellsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ wells: Well[] }>('/wells', { ...filters });
      return res.wells;
    },
  });
}

export function useWell(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? wellsKeys.byId(projectId, id) : ['wells', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ well: Well }>(`/wells/${id}`);
      return res.well;
    },
  });
}

export interface WellInput {
  property_number: string;
  property_name?: string | null | undefined;
  api?: string | null | undefined;
  alt_prop_number?: string | null | undefined;
  prospect?: string | null | undefined;
  field?: string | null | undefined;
  rsrv_cat?: string | null | undefined;
  abstract?: string | null | undefined;
  survey?: string | null | undefined;
  roll_up?: boolean | undefined;
  reviewer_user_id?: string | null | undefined;

  well_status?: string | null | undefined;
  well_type?: string | null | undefined;
  unit_type?: string | null | undefined;
  fed_rights?: string | null | undefined;
  unit_acreage?: number | null | undefined;
  do_number?: string | null | undefined;
  owner?: string | null | undefined;
  operator?: string | null | undefined;

  county?: string | null | undefined;
  state?: string | null | undefined;
  township?: string | null | undefined;
  range?: string | null | undefined;
  section?: string | null | undefined;
  block?: string | null | undefined;

  description?: string | null | undefined;
  flag_for_issues?: string | null | undefined;
  exception_remark?: string | null | undefined;
  title_issues?: string | null | undefined;

  oil_bpo_wi?: number | null | undefined;
  oil_apo_wi?: number | null | undefined;
  oil_bpo_nri?: number | null | undefined;
  oil_apo_nri?: number | null | undefined;
  oil_bpo_orri?: number | null | undefined;
  oil_apo_orri?: number | null | undefined;
  oil_bpo_ri?: number | null | undefined;
  oil_apo_ri?: number | null | undefined;
  gas_bpo_wi?: number | null | undefined;
  gas_apo_wi?: number | null | undefined;
  gas_bpo_nri?: number | null | undefined;
  gas_apo_nri?: number | null | undefined;
  gas_bpo_orri?: number | null | undefined;
  gas_apo_orri?: number | null | undefined;
  gas_bpo_ri?: number | null | undefined;
  gas_apo_ri?: number | null | undefined;

  minerals?: boolean | undefined;
  royalties?: boolean | undefined;
  participating?: boolean | undefined;
  non_participating?: boolean | undefined;
  term?: boolean | undefined;
  perpetual?: boolean | undefined;
  call_on_prod?: boolean | undefined;
  non_consent?: boolean | undefined;
  non_consent_penalty?: string | null | undefined;
  payout?: boolean | undefined;
  payout_amount?: string | null | undefined;
  payout_date?: string | null | undefined;

  first_prod?: string | null | undefined;
  last_prod?: string | null | undefined;
  oil_data_as_of?: string | null | undefined;
  oil_cum?: number | null | undefined;
  oil_avg_bpm_6mo?: number | null | undefined;
  oil_avg_bopd_6mo?: number | null | undefined;
  gas_data_as_of?: string | null | undefined;
  gas_cum?: number | null | undefined;
  gas_avg_mcf_6mo?: number | null | undefined;
  gas_avg_mcfd_6mo?: number | null | undefined;
  num_completions?: number | null | undefined;
  total_depth?: number | null | undefined;
  prod_zones?: string | null | undefined;

  remarks?: string | null | undefined;
  reviewer_notes?: string | null | undefined;
  change_remarks?: string | null | undefined;
}

export function useCreateWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WellInput) => api.post<{ well: Well }>('/wells', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: wellsKeys.all }),
  });
}

export function useUpdateWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<WellInput> }) =>
      api.patch<{ well: Well }>(`/wells/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: wellsKeys.all }),
  });
}

export function useDeleteWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/wells/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: wellsKeys.all }),
  });
}
